import axios from 'axios';

// Production da relative URL ishlatiladi, development da localhost
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - token qo'shish
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - token yangilash
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login/', data),
  logout: (refresh) => api.post('/auth/logout/', { refresh }),
  getCurrentUser: () => api.get('/auth/me/'),
  refreshToken: (refresh) => api.post('/auth/refresh/', { refresh }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/admin/dashboard/'),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users/', { params }),
  getById: (id) => api.get(`/users/${id}/`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.patch(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
};

// Subjects (Fanlar) API
export const subjectsAPI = {
  getAll: (params) => api.get('/subjects/', { params }),
  getSimple: () => api.get('/subjects/simple/'),
  getById: (id) => api.get(`/subjects/${id}/`),
  create: (data) => api.post('/subjects/', data),
  update: (id, data) => api.patch(`/subjects/${id}/`, data),
  delete: (id) => api.delete(`/subjects/${id}/`),
};

// Groups API
export const groupsAPI = {
  getAll: (params) => api.get('/groups/', { params }),
  getSimple: () => api.get('/groups/simple/'),
  getById: (id) => api.get(`/groups/${id}/`),
  create: (data) => api.post('/groups/', data),
  update: (id, data) => api.patch(`/groups/${id}/`, data),
  delete: (id) => api.delete(`/groups/${id}/`),
  assignSubjects: (id, subjectIds) => api.post(`/groups/${id}/assign_subjects/`, { subject_ids: subjectIds }),
};

// Students API
export const studentsAPI = {
  getAll: (params) => api.get('/students/', { params }),
  getById: (id) => api.get(`/students/${id}/`),
  create: (data) => api.post('/students/', data),
  update: (id, data) => api.put(`/students/${id}/`, data),
  delete: (id) => api.delete(`/students/${id}/`),
  resetPassword: (id) => api.post(`/students/${id}/reset_password/`),
};

// Teachers API
export const teachersAPI = {
  getAll: (params) => api.get('/teachers/', { params }),
  getById: (id) => api.get(`/teachers/${id}/`),
  create: (data) => api.post('/teachers/', data),
  update: (id, data) => api.put(`/teachers/${id}/`, data),
  delete: (id) => api.delete(`/teachers/${id}/`),
  resetPassword: (id) => api.post(`/teachers/${id}/reset_password/`),
  assignSubjects: (id, subjectIds) => api.post(`/teachers/${id}/assign_subjects/`, { subject_ids: subjectIds }),
  assignGroups: (id, groupIds) => api.post(`/teachers/${id}/assign_groups/`, { group_ids: groupIds }),
};

// Teacher Panel API
export const teacherPanelAPI = {
  getDashboard: () => api.get('/teacher/dashboard/'),
  getGroups: () => api.get('/teacher/groups/'),
  getGroupStudents: (groupId) => api.get(`/teacher/groups/${groupId}/students/`),
  getSubjects: () => api.get('/teacher/subjects/'),
  getProfile: () => api.get('/teacher/profile/'),
  updateProfile: (data) => api.patch('/teacher/profile/', data),
  
  // Materials
  getMaterials: (subjectId) => api.get('/teacher/materials/', { params: subjectId ? { subject: subjectId } : {} }),
  uploadMaterial: (formData) => api.post('/teacher/materials/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMaterial: (id) => api.delete(`/teacher/materials/${id}/`),
};

// Subject Materials (for students)
export const materialsAPI = {
  getBySubject: (subjectId) => api.get(`/subjects/${subjectId}/materials/`),
};

// Student Panel API
export const studentPanelAPI = {
  getDashboard: () => api.get('/student/dashboard/'),
  getProfile: () => api.get('/student/profile/'),
  updateProfile: (data) => api.patch('/student/profile/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSubjects: () => api.get('/student/subjects/'),
  getSubjectMaterials: (subjectId, type) => api.get(`/student/subjects/${subjectId}/materials/`, { 
    params: type ? { type } : {} 
  }),
  getGroupmates: () => api.get('/student/groupmates/'),
  // Temperament test
  getTemperament: () => api.get('/student/temperament/'),
  submitTemperament: (answers) => api.post('/student/temperament/submit/', { answers }),
  getTemperamentResult: () => api.get('/student/temperament/result/'),
  // Language test
  getLanguageTest: (language) => api.get(`/student/language/${language}/`),
  submitLanguageTest: (language, answers) => api.post('/student/language-submit/', { language, answers }),
  getLanguageResults: () => api.get('/student/language-results/'),
  // Regulations
  getRegulations: (params) => api.get('/student/regulations/', { params }),
  // Suicide test
  getSuicideTest: () => api.get('/student/suicide-test/'),
  submitSuicideTest: (data) => api.post('/student/suicide-test/', data),
};

// Admin Temperament API
export const temperamentAPI = {
  getResults: (params) => api.get('/admin/temperament/', { params }),
};

// Admin Language API
export const languageAPI = {
  getResults: (params) => api.get('/admin/language/', { params }),
};

// Clubs API (admin)
export const clubsAPI = {
  getAll: (params) => api.get('/clubs/', { params }),
  get: (id) => api.get(`/clubs/${id}/`),
  create: (data) => api.post('/clubs/', data),
  update: (id, data) => api.patch(`/clubs/${id}/`, data),
  delete: (id) => api.delete(`/clubs/${id}/`),
  getMembers: (id) => api.get(`/clubs/${id}/members/`),
};

// Student Clubs API
export const studentClubsAPI = {
  getAll: (params) => api.get('/student/clubs/', { params }),
  getMyClubs: () => api.get('/student/clubs/my/'),
  join: (clubId) => api.post(`/student/clubs/${clubId}/join/`),
  leave: (clubId) => api.post(`/student/clubs/${clubId}/leave/`),
};

// Student Corruption API
export const studentCorruptionAPI = {
  getAll: () => api.get('/student/corruption/'),
  create: (data) => api.post('/student/corruption/', data),
};

// Admin Corruption API
export const adminCorruptionAPI = {
  getAll: (params) => api.get('/admin/corruption/', { params }),
  get: (id) => api.get(`/admin/corruption/${id}/`),
  update: (id, data) => api.patch(`/admin/corruption/${id}/`, data),
};

// Leadership API (admin)
export const leadershipAPI = {
  getAll: (params) => api.get('/leadership/', { params }),
  get: (id) => api.get(`/leadership/${id}/`),
  create: (data) => api.post('/leadership/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.patch(`/leadership/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/leadership/${id}/`),
};

// Leadership Appeals API (admin)
export const adminLeadershipAppealsAPI = {
  getAll: (params) => api.get('/admin/leadership/appeals/', { params }),
  update: (id, data) => api.patch(`/admin/leadership/appeals/${id}/`, data),
};

// Student Leadership API
export const studentLeadershipAPI = {
  getAll: () => api.get('/student/leadership/'),
  getAppeals: () => api.get('/student/leadership/appeals/'),
  createAppeal: (data) => api.post('/student/leadership/appeals/', data),
};

// Events API (Admin)
export const eventsAPI = {
  getAll: (params) => api.get('/events/', { params }),
  get: (id) => api.get(`/events/${id}/`),
  create: (data) => api.post('/events/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.patch(`/events/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/events/${id}/`),
};

// Student Events API
export const studentEventsAPI = {
  getAll: (params) => api.get('/student/events/', { params }),
  get: (id) => api.get(`/student/events/${id}/`),
};

// Language Certificates API (Student)
export const studentCertificatesAPI = {
  getAll: () => api.get('/student/certificates/'),
  get: (id) => api.get(`/student/certificates/${id}/`),
  create: (data) => api.post('/student/certificates/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.patch(`/student/certificates/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/student/certificates/${id}/`),
};

// Language Certificates API (Admin)
export const adminCertificatesAPI = {
  getAll: (params) => api.get('/admin/certificates/', { params }),
  verify: (id, isVerified) => api.patch(`/admin/certificates/${id}/`, { is_verified: isVerified }),
  delete: (id) => api.delete(`/admin/certificates/${id}/`),
};

// Regulations API (Admin)
export const adminRegulationsAPI = {
  getAll: (params) => api.get('/admin/regulations/', { params }),
  get: (id) => api.get(`/admin/regulations/${id}/`),
  create: (data) => api.post('/admin/regulations/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.patch(`/admin/regulations/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/regulations/${id}/`),
};

// Suicide Test API (Admin)
export const adminSuicideTestAPI = {
  getAll: (params) => api.get('/admin/suicide-test/', { params }),
  get: (id) => api.get(`/admin/suicide-test/${id}/`),
  update: (id, data) => api.patch(`/admin/suicide-test/${id}/`, data),
};

export default api;
